import { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Phone, Mail, Instagram, MapPin, Send, CheckCircle2 } from "lucide-react";
import { Reveal, Kicker } from "@/components/Reveal";
import api from "@/lib/api";

export default function Contact({ lang }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError("");
    try {
      await api.post("/contact", { ...form, lang });
      setStatus("done");
    } catch (err) {
      setStatus("idle");
      setError(t("estimate.error"));
    }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const cards = [
    { icon: Phone, label: t("contactPage.phone"), value: "786-774-6692", href: "tel:17867746692", tid: "contact-phone" },
    { icon: Mail, label: t("contactPage.email"), value: "info@vialfa.com", href: "mailto:info@vialfa.com", tid: "contact-email" },
    { icon: Instagram, label: t("contactPage.instagram"), value: "@vialfaservices", href: "https://instagram.com/vialfaservices", tid: "contact-instagram" },
    { icon: MapPin, label: t("contactPage.area"), value: "Miami, FL 33130", href: null, tid: "contact-area" },
  ];

  return (
    <>
      <section data-testid="contact-hero" className="pt-40 pb-16 md:pt-52 md:pb-24 px-6 md:px-12 bg-navy text-offwhite">
        <div className="max-w-[1400px] mx-auto">
          <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}>
            <Kicker light>{t("contactPage.kicker")}</Kicker>
            <h1 className="mt-6 font-heading font-black uppercase tracking-tighter text-4xl sm:text-6xl lg:text-7xl leading-[0.95]">
              {t("contactPage.title")}
            </h1>
            <p className="mt-6 text-offwhite/60 max-w-xl">{t("contactPage.sub")}</p>
          </motion.div>
        </div>
      </section>

      <section data-testid="contact-body" className="py-16 md:py-24 px-6 md:px-12">
        <div className="max-w-[1400px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {cards.map((c, i) => (
                <Reveal key={i} delay={i * 0.08}>
                  <a
                    href={c.href || undefined}
                    target={c.href?.startsWith("http") ? "_blank" : undefined}
                    rel="noopener noreferrer"
                    data-testid={c.tid}
                    className={`block bg-white border border-sandline p-6 h-full ${c.href ? "hover:border-orange/50 transition-colors duration-300" : ""}`}
                  >
                    <c.icon size={22} className="text-orange mb-4" strokeWidth={1.6} />
                    <p className="text-xs font-bold uppercase tracking-[0.2em] text-slateblue">{c.label}</p>
                    <p className="mt-1 font-heading font-bold">{c.value}</p>
                  </a>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.2} className="mt-6">
              <div className="overflow-hidden border border-sandline" data-testid="contact-map">
                <iframe
                  title="VIALFA service area — Miami, FL"
                  src="https://www.google.com/maps?q=Miami,+FL+33130&output=embed"
                  className="w-full h-72 grayscale-[35%] contrast-[1.05]"
                  loading="lazy"
                />
              </div>
            </Reveal>
            <Reveal delay={0.25} className="mt-6">
              <p className="text-sm text-slateblue leading-relaxed">{t("footer.areaText")}</p>
            </Reveal>
          </div>

          <Reveal delay={0.1} className="lg:col-span-7">
            <div className="bg-white border border-sandline p-8 md:p-12">
              {status === "done" ? (
                <div className="text-center py-12" data-testid="contact-success">
                  <CheckCircle2 size={48} className="text-orange mx-auto mb-6" strokeWidth={1.5} />
                  <h2 className="font-heading font-bold text-2xl tracking-tight">{t("contactPage.successTitle")}</h2>
                  <p className="mt-3 text-slateblue max-w-md mx-auto">{t("contactPage.successBody")}</p>
                  <button
                    onClick={() => { setStatus("idle"); setForm({ name: "", email: "", phone: "", message: "" }); }}
                    data-testid="contact-another-btn"
                    className="mt-8 text-sm font-bold uppercase tracking-widest text-orange hover:text-orange-bright transition-colors duration-200"
                  >
                    {t("contactPage.another")}
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="contact-form">
                  <h2 className="font-heading font-bold text-2xl tracking-tight mb-8">{t("contactPage.formTitle")}</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-[0.2em] text-slateblue mb-2">{t("contactPage.name")} *</label>
                      <input
                        required value={form.name} onChange={set("name")}
                        data-testid="contact-name-input"
                        className="w-full border border-sandline bg-offwhite px-4 py-3 text-navy focus:outline-none focus:border-orange transition-colors duration-200"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-[0.2em] text-slateblue mb-2">{t("contactPage.emailField")} *</label>
                      <input
                        required type="email" value={form.email} onChange={set("email")}
                        data-testid="contact-email-input"
                        className="w-full border border-sandline bg-offwhite px-4 py-3 text-navy focus:outline-none focus:border-orange transition-colors duration-200"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-bold uppercase tracking-[0.2em] text-slateblue mb-2">{t("contactPage.phoneField")}</label>
                      <input
                        value={form.phone} onChange={set("phone")}
                        data-testid="contact-phone-input"
                        className="w-full border border-sandline bg-offwhite px-4 py-3 text-navy focus:outline-none focus:border-orange transition-colors duration-200"
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-bold uppercase tracking-[0.2em] text-slateblue mb-2">{t("contactPage.message")} *</label>
                      <textarea
                        required rows={5} value={form.message} onChange={set("message")}
                        data-testid="contact-message-input"
                        className="w-full border border-sandline bg-offwhite px-4 py-3 text-navy focus:outline-none focus:border-orange transition-colors duration-200 resize-none"
                      />
                    </div>
                  </div>
                  {error && <p className="mt-4 text-sm text-red-600" data-testid="contact-error">{error}</p>}
                  <button
                    type="submit"
                    disabled={status === "sending"}
                    data-testid="contact-submit-btn"
                    className="mt-8 inline-flex items-center gap-3 bg-orange hover:bg-orange-bright disabled:opacity-60 text-white font-bold px-8 py-4 text-sm uppercase tracking-widest transition-[background-color,transform] duration-200 hover:scale-105 active:scale-95"
                  >
                    {status === "sending" ? t("contactPage.sending") : t("contactPage.send")} <Send size={15} />
                  </button>
                </form>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
